import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {gameUiTexts} from 'data/ui-texts';
// import {defaultMaxPoints} from 'data/points-data';
import {
	getTaskData,
	checkIfGroupHasCompletedTask, 
	calculateErrors, 
	calculatePoints
} from 'helpers/game-helper';
import Button from 'components/ui/button/button';
import Sort from 'components/game-engines/sort/sort';
import MultipleChoice from 'components/game-engines/multiple-choice/multiple-choice';
import Order from 'components/game-engines/order/order';
import InputAnswer from 'components/game-engines/input-answer/input-answer';
import PaperDoll from 'components/game-engines/paper-doll/paper-doll';
import SpotErrors from 'components/game-engines/spot-errors/spot-errors';
import Pairs from 'components/game-engines/pairs/pairs';
import './assignment.scss';
import appConfig from 'config/app.config';

const Assignment = (
	{
		taskNumber,
		totalTasks,
		playerData, 
		scenarioId, 
		taskId,
		updatePlayerData,
		setPopupFileData,
		setPopupData,
		goToNextTask
	}) => {


	// Task data 
	const taskData = getTaskData(scenarioId, taskId);
	
	// Task difficulty 
	const difficulty = taskData.difficulty ? taskData.difficulty : 'medium';
	const difficultyText = (gameUiTexts.difficulties.hasOwnProperty(difficulty)
		? gameUiTexts.difficulties[difficulty]
		: gameUiTexts.difficulties['medium']
	);

	let timeout = null;
	

	useEffect(() => {
		// Clear timeout if any on unmount
		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* No data found - return null */
	if (!taskData) return null;

	/* Check if task is completed */
	const taskIsCompleted = checkIfGroupHasCompletedTask(taskData.taskId, playerData);

	/* Get player data for this task */
	let playerTaskData = null;
	let attemptNumber = 1;
	// let awardedPoints = null;
	if (
		playerData.assignments &&
		playerData.assignments.some((a) => {
			return a.taskId === taskData.taskId;
		}) 
	) {
		const groupAssignment = playerData.assignments.find((a) => {
			return a.taskId === taskData.taskId;
		});
		// if (taskIsCompleted) {
		// 	awardedPoints = groupAssignment.points;
		// }
		if (groupAssignment.attempts && groupAssignment.attempts.length > 0) {
			playerTaskData = groupAssignment.attempts[groupAssignment.attempts.length - 1];
			attemptNumber = groupAssignment.attempts.length + 1;
		}
	}
	

	/* Get max possible / awarded points */
	// let maxPoints = defaultMaxPoints;
	// if (taskIsCompleted) {
	// 	maxPoints = awardedPoints;
	// } else {
	// 	if (playerTaskData) {
	// 		const groupAssignment = playerData.assignments.find((a) => {
	// 			return a.taskId === taskData.taskId;
	// 		});

	// 		/* Calculate errors */
	// 		let errors = calculateErrors(taskData.type, groupAssignment.attempts);
	// 		if (taskData.type === 'order') errors += 1;

	// 		/* Calculate max possible points (i.e. if they make no more mistakes) */
	// 		 maxPoints = calculatePoints(taskData.type, errors, taskData.maxPoints);
	// 	}
	// }

	/* Task type component */
	let TaskComponent = null;
	if (taskData.type === 'sort') TaskComponent = Sort;
	else if (taskData.type === 'order') TaskComponent = Order;
	else if (taskData.type === 'input-answer') TaskComponent = InputAnswer;
	else if (taskData.type === 'multiple-choice') TaskComponent = MultipleChoice;
	else if (taskData.type === 'paper-doll') TaskComponent = PaperDoll;
	else if (taskData.type === 'spot-errors') TaskComponent = SpotErrors;
	else if (taskData.type === 'pairs') TaskComponent = Pairs;
	
	/**
	 * Handle update task
	 * @param {string} type 
	 * @param {object} taskAnswerData 
	 */
	const handleUpdateTask = (type, taskAnswerData) => {
		/* Get playerData data for task */
		let groupAssignmentsData = (playerData.assignments ? playerData.assignments : []);
		let currentAssignmentIndex = groupAssignmentsData.findIndex((a) => {
			return a.taskId === taskData.taskId;
		});
		if (currentAssignmentIndex < 0) {
			/* First attempt */
			groupAssignmentsData.push({
				isCompleted: false,
				taskId: taskData.taskId,
				type: type,
				attempts: [{
					...taskAnswerData
				}]
			});	
		} else {
			/* Not first attempt */
			groupAssignmentsData[currentAssignmentIndex].attempts.push({...taskAnswerData});
		}

		/* Update playerData data */
		updatePlayerData({assignments: groupAssignmentsData});
	};


	/**
	 * Handle complete task
	 */
	const handleCompleteTask = (type, taskAnswerData) => {
		/* Get playerData data for task */
		let groupAssignmentsData = (playerData.assignments ? playerData.assignments : []);
		const currentAssignmentIndex = groupAssignmentsData.findIndex((a) => {
			return a.taskId === taskData.taskId;
		});

		/* Attempts data */
		let attempts = [];
		if (currentAssignmentIndex >= 0) {
			attempts = groupAssignmentsData[currentAssignmentIndex].attempts;
		}
		attempts.push({...taskAnswerData});

		/* Calculate errors */
		const errors = calculateErrors(type, attempts);
		
		/* Calculate points */
		const points = calculatePoints(type, errors, taskData.maxPoints, taskAnswerData);

		if (currentAssignmentIndex < 0) {
			/* First time playerData updates/completes task */
			groupAssignmentsData.push({
				isCompleted: true,
				taskId: taskData.taskId,
				type: type,
				points: points,
				attempts: attempts
			});
		} else {
			/* Not first time playerData completes task */
			groupAssignmentsData[currentAssignmentIndex].isCompleted = true;
			groupAssignmentsData[currentAssignmentIndex].points = points;
			groupAssignmentsData[currentAssignmentIndex].attempts = attempts;
		}

		// Open assignment popup if popup text exists in assignment data
		if (taskData.popupText) {
			timeout = setTimeout(() => {
				setPopupData({text: taskData.popupText, title: taskData.title});
			}, 500);
		}

		/* Update playerData data */
		updatePlayerData({assignments: groupAssignmentsData});
	};

	return (
		<div className={'Assignment ' + taskData.type}>
			<div className={'Assignment-header ' + scenarioId}>
				<div className='Assignment-difficulty'>
					<div className='Assignment-difficultyText'>
						<span>{difficultyText}</span>
					</div>
					<div className='Assignment-difficultyStars'>
						<div className={'Assignment-difficultyStar ' 
							+ (difficulty === 'hard' ? 'red' : difficulty === 'medium' ? 'orange' : 'green')} />
						<div className={'Assignment-difficultyStar '
							+ (difficulty === 'hard' ? 'red' : difficulty === 'medium' ? 'orange' : '')} />
						<div className={'Assignment-difficultyStar '
							+ (difficulty === 'hard' ? 'red' : '')} />
					</div>
				</div>
				{/* <div className='Assignment-maxPoints'>
					<span>{gameUiTexts.points + ': ' + maxPoints}</span>
				</div> */}
			</div>
			<div className="Assignment-content">
				{TaskComponent && <TaskComponent 
					scenarioId={scenarioId}
					isCompleted={taskIsCompleted}
					attemptNumber={attemptNumber}
					playerTaskData={playerTaskData}
					taskData={taskData}
					setPopupFileData={setPopupFileData}
					handleUpdateTask={handleUpdateTask}
					handleCompleteTask={handleCompleteTask}
				/>}
			
				{taskIsCompleted && 
					<div className='Assignment-navigation'>
						<div className='Assignment-backToOffice'>
							<Button 
								text={gameUiTexts.continueBtn}
								classes={['blueRounded', 'next', scenarioId]}
								onClick={() => {goToNextTask();}}
							/>
						</div>
					</div>
				}
				{(!taskIsCompleted && (appConfig.env === 'development' || appConfig.env === 'test')) && 
					<div className="Assignment-skipBtn">
						<Button 
							text={'Skip'}
							classes={['blueRounded', 'next', scenarioId]}
							onClick={() => {goToNextTask();}}
						/>
					</div>
				}
			</div>

		</div>
	);
};

Assignment.propTypes = {
	taskNumber: PropTypes.number.isRequired,
	totalTasks: PropTypes.number.isRequired,
	playerData: PropTypes.object.isRequired,
	scenarioId: PropTypes.string.isRequired,
	taskId: PropTypes.string.isRequired,
	updatePlayerData: PropTypes.func.isRequired,
	goToNextTask: PropTypes.func.isRequired,
	setPopupFileData: PropTypes.func.isRequired,
	setPopupData: PropTypes.func.isRequired,
};

export default Assignment;
